/**
 * HeaderAnalyticsFunction.ts
 * Common analytics functions for header components
 */

import {
  dlEventConstants,
  componentNameConstants,
  dlElementConstants,
  headerConstants,
} from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import type { DataLayerProps } from "@repo/analytics/data-layer-utils";
import { trackAnalyticsEvent } from "../../utils/AnalyticsUtils";

/**
 * Handle notification delete click analytics
 * Logs click event for the notification delete icon
 */
export const handleHeaderNotificationDeleteDL = (
  cta_text: string,
  section_title: string = "Notifications",
  tab_position: string = "4"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: dlElementConstants.NOTIFICATION_DELETE_ICON,
    cta_text,
    section_title,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderNotificationDelete");
};

/**
 * Handle notification bell icon click analytics
 * Logs click event for the notification bell icon in the header
 */
export const handleHeaderNotificationBellDL = (
  cta_text: string,
  section_title: string = "Notifications",
  tab_position: string = "3"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: dlElementConstants.NOTIFICATION_BELL_ICON,
    cta_text,
    section_title,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderNotificationBell");
};

/**
 * Handle notification close icon click analytics
 * Logs click event for the notification panel close icon
 */
export const handleHeaderNotificationCloseDL = (
  cta_text: string,
  section_title: string = "Notifications",
  tab_position: string = "5"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.NOTIFICATION_CLOSE_ICON,
    cta_text,
    section_title,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderNotificationClose");
};

/**
 * Handle header sign in click analytics
 * Logs click event for the sign in button/icon in header
 */
export const handleHeaderSignInClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.HEADER_SIGN_IN,
  heading?: string,
  destination_url?: string,
  tab_position: string = "1"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.HEADER_SIGN_IN_ICON,
    cta_text,
    section_title: heading || headerConstants.SECTION_TITLES.HEADER,
    destination_url,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderSignIn");
};

/**
 * Handle EMI card click analytics in header
 * Logs click event for the EMI card in header
 */
export const handleHeaderEmiCardClickDL = (
  section_title: string,
  destination_url: string,
  cta_text: string = headerConstants.CTA_TEXT.EMI_CARD,
  tab_position: string = "2"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.HEADER_EMI_ICON,
    cta_text,
    section_title,
    tab_position,
    destination_url,
  };

  trackAnalyticsEvent(data, undefined, "HeaderEmiCard");
};

/**
 * Handle Cart icon click analytics in header
 * Logs click event for the cart icon in header
 */
export const handleHeaderCartClickDL = (
  section_title: string,
  destination_url: string,
  cta_text: string = headerConstants.CTA_TEXT.HEADER_CART,
  tab_position: string = "6"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.HEADER_CART_ICON,
    cta_text,
    destination_url,
    section_title,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderCart");
};

/**
 * Handle partner icon click analytics in header
 * Logs click event for the partner icon in header
 */
export const handleHeaderPartnerClickDL = (
  section_title: string,
  cta_text: string,
  destination_url?: string,
  tab_position: string = "8"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.HEADER_PARTNER_ICON,
    cta_text,
    section_title,
    tab_position,
    destination_url,
  };

  trackAnalyticsEvent(data, undefined, "HeaderPartner");
};

/**
 * Handle prime/rewards icon click analytics in header
 * Logs click event for the prime/rewards icon in header
 */
export const handleHeaderPrimeClickDL = (
  cta_text: string,
  destination_url: string,
  section_title: string = "prime",
  tab_position: string = "7"
) => {
  const data: DataLayerProps = {
    component: componentNameConstants.TOP_NAVIGATION_BAR_HEADER,
    event: "icon",
    event_type: dlEventConstants.CLICK,
    element: headerConstants.ELEMENTS.HEADER_REWARDS_ICON,
    cta_text,
    destination_url,
    section_title,
    tab_position,
  };

  trackAnalyticsEvent(data, undefined, "HeaderPrime");
};

/**
 * Handle header menu button click analytics
 * Logs click event for the header menu/sidebar button
 */
export const handleHeaderMenuClickDL = (cta_text: string = headerConstants.CTA_TEXT.MENU) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle header menu close button click analytics
 * Logs click event for the close (X) button in the sidebar menu
 */
export const handleHeaderMenuCloseClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.CLOSE_MENU
) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle main menu item click analytics
 * Logs click event for main menu items in the sidebar
 */
export const handleHeaderMainMenuClickDL = (menuTitle: string, source: string = "main") => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    cta_text: menuTitle,
    menu_source: source,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    menu_source: data.menu_source,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle sidebar sign in/profile link click analytics
 * Logs click event for the sign in or profile link in the sidebar
 */
export const handleHeaderSidebarProfileClickDL = (url: string, cta_text: string) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    destination_url: url,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle sidebar main menu button click analytics
 * Logs click event for main menu buttons in the sidebar
 */
export const handleHeaderSidebarMenuButtonClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    destination_url: "", // Menu buttons don't have direct URLs
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};
